// Supply Network panel: body for the "Non-app dealer locations" tab.
// Sister to ManufacturingPlantSubscriberLocationsBody. Renders the
// filters + bulk bar + select-all bar + rows for dealer locations
// stored in mfg_dealer_locations (manufacturer-owned roster of dealers
// who don't subscribe to the platform).
//
// Extracted from the old ManufacturingPlantDealerLocationsSection so
// the two tabs of the unified Dealer-locations slider can share one
// SectionCard / search field at the wrapper level.
import { PaginationBar } from "@/components/common/PaginationBar";
import { usePagination } from "@/components/common/usePagination";
import type { MfgDealerAddress, MfgDealerLocation } from "@/lib/manufacturing/dealerTypes";
import { ManufacturingPlantDealerLocationsBulkBar } from "./ManufacturingPlantDealerLocationsBulkBar";
import { ManufacturingPlantDealerLocationsFilters } from "./ManufacturingPlantDealerLocationsFilters";
import { ManufacturingPlantDealerLocationsReassignConfirm } from "./ManufacturingPlantDealerLocationsReassignConfirm";
import type { UseManufacturingPlantDealerLocations } from "./useManufacturingPlantDealerLocations";
import { mfgXsInputClass } from "@/components/manufacturing/manufacturingSurfaceClasses";

function addressLine(loc: MfgDealerLocation): string {
  const addr = (loc.address as MfgDealerAddress) ?? {};
  return [addr.city, addr.state, addr.postal_code]
    .filter((x) => typeof x === "string" && x)
    .join(", ");
}

function LocationRow({
  loc,
  m,
}: {
  loc: MfgDealerLocation;
  m: UseManufacturingPlantDealerLocations;
}) {
  const isSelected = m.selectedIds.has(loc.id);
  const eff = m.effectivePlantFor(loc.id);
  const directValue = loc.primary_plant_id ?? "";
  const linkedOrgName = loc.linked_dealer_org_id
    ? m.dealerOrgNameById.get(loc.linked_dealer_org_id)
    : null;
  const addr = addressLine(loc);
  const chipClass =
    eff?.source === "direct"
      ? "bg-indigo-600 text-white"
      : eff?.source === "none"
        ? "bg-gray-200 text-gray-700 dark:bg-zinc-700 dark:text-zinc-200"
        : "bg-gray-100 text-gray-700 dark:bg-zinc-800 dark:text-zinc-200 border border-gray-300 dark:border-zinc-700";
  return (
    <li className="flex items-start gap-3 py-2">
      <label className="flex pt-1">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => m.toggleSelected(loc.id)}
          aria-label={`Select ${loc.name}`}
          className="h-4 w-4 rounded border-gray-300"
        />
      </label>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="font-medium text-gray-900 dark:text-zinc-100">{loc.name}</span>
          {!loc.is_active ? (
            <span className="rounded-md bg-gray-400 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
              Inactive
            </span>
          ) : null}
          {linkedOrgName ? (
            <span className="rounded-md bg-emerald-600 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
              {linkedOrgName}
            </span>
          ) : null}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-zinc-300">
            <span className="sr-only">Plant for {loc.name}</span>
            <select
              className={mfgXsInputClass}
              value={directValue}
              onChange={(e) =>
                m.requestRowReassign(loc, e.target.value === "" ? null : e.target.value)
              }
              disabled={m.actionBusy}
            >
              <option value="">(inherit / clear direct)</option>
              {m.plants
                .filter((p) => p.is_active)
                .map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.plant_code} · {p.name}
                  </option>
                ))}
            </select>
          </label>
          {eff ? (
            <span
              className={`rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${chipClass}`}
              title={
                eff.source === "direct"
                  ? "This location has a direct plant override"
                  : eff.sourceLabel
              }
            >
              {eff.source === "direct" ? "Direct" : "Inherited"}: {eff.plantLabel}
            </span>
          ) : null}
        </div>
        {eff && eff.source !== "direct" && eff.sourceLabel ? (
          <div className="mt-0.5 text-[11px] italic text-gray-500 dark:text-zinc-400">
            {eff.sourceLabel}
          </div>
        ) : null}
        {addr ? (
          <div className="mt-0.5 text-xs text-gray-600 dark:text-zinc-400">{addr}</div>
        ) : null}
      </div>
    </li>
  );
}

export function ManufacturingPlantNonAppLocationsBody({
  m,
}: {
  m: UseManufacturingPlantDealerLocations;
}) {
  const total = m.dealerLocations.length;
  const visible = m.filteredLocations.length;
  // Slice 12: client-side pagination, default 5 per page.
  const pager = usePagination(m.filteredLocations);
  return (
    <>
      <ManufacturingPlantDealerLocationsFilters m={m} />

      {m.localError ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900 dark:border-red-800 dark:bg-red-950/35 dark:text-red-100">
          {m.localError}
        </div>
      ) : null}

      <ManufacturingPlantDealerLocationsBulkBar m={m} />

      <div className="flex flex-wrap items-center gap-3 text-xs text-gray-600 dark:text-zinc-400">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={m.allVisibleSelected}
            onChange={() => {
              if (m.allVisibleSelected) m.clearSelection();
              else m.selectAllFiltered();
            }}
            aria-label="Select all visible"
            className="h-4 w-4 rounded border-gray-300"
            disabled={visible === 0}
          />
          Select all visible (across pages)
        </label>
      </div>

      {m.listBusy ? (
        <div className="text-sm text-gray-500 dark:text-zinc-400">
          Loading non-app dealer locations…
        </div>
      ) : null}
      {!m.listBusy && total === 0 ? (
        <div className="text-sm text-gray-500 dark:text-zinc-400">
          No non-app dealer locations yet. Add them via Logistics → Dealers,
          then return here to assign plants.
        </div>
      ) : null}
      {!m.listBusy && total > 0 && visible === 0 ? (
        <div className="text-sm text-gray-500 dark:text-zinc-400">
          No locations match the current filters. Clear the search or filters
          to widen the list.
        </div>
      ) : null}

      <ul className="divide-y divide-gray-100 dark:divide-zinc-700">
        {pager.paginatedItems.map((loc) => (
          <LocationRow key={loc.id} loc={loc} m={m} />
        ))}
      </ul>

      <PaginationBar
        pageIndex={pager.pageIndex}
        pageCount={pager.pageCount}
        pageSize={pager.pageSize}
        totalCount={pager.totalCount}
        firstShown={pager.firstShown}
        lastShown={pager.lastShown}
        onPageChange={pager.setPageIndex}
        onPageSizeChange={pager.setPageSize}
        noun="non-app locations"
        alwaysShow
      />

      <ManufacturingPlantDealerLocationsReassignConfirm
        open={m.confirmOpen}
        reassignments={m.confirmReassignments}
        netNewAssignmentCount={m.confirmNetNewCount}
        targetPlantLabel={m.confirmTargetLabel}
        busy={m.actionBusy}
        onCancel={m.cancelConfirm}
        onConfirm={() => void m.submitBulkApply()}
      />
    </>
  );
}
